import { useCallback, useContext } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import { Auth0Context } from 'lane-shared/contexts/Auth0Context';

import { buildRedirectServiceReturnTo } from '../components/onboarding/Onboarding/useAuthClearSession';
import logout from '../helpers/logout';
import { useGetAuthToken } from './useGetAuthToken';

/**
 * Marks that we already bounced through `loginWithRedirect` to recover this
 * tab session. `loginWithRedirect` is a full-page navigation, so a failed
 * re-login can only be detected on the next load (the SDK errors again) —
 * this flag, persisted across that redirect, is how we know not to loop and
 * to escalate to a full logout instead.
 */
const RECOVERY_LOGIN_ATTEMPTED_KEY = 'auth0_recovery_login_attempted';

/**
 * True when the Auth0 SPA SDK has a cached session in local storage, i.e. the
 * user was logged in. Used to tell "session broke" apart from "never logged
 * in" so we don't bounce anonymous visitors through a recovery login.
 */
export function hasStoredAuth0Session(): boolean {
  return Object.keys(localStorage).some(key =>
    key.startsWith('@@auth0spajs@@')
  );
}

/**
 * Recovers from an unrenewable Auth0 session without making the user manually
 * clear local storage:
 *
 * 1. First failure this tab session → `loginWithRedirect`. The refresh token
 *    is dead but the Auth0 SSO session may still be alive, in which case a
 *    top-level redirect renews it with no credential prompt.
 * 2. If we already tried that re-login and the session is still broken → full
 *    Auth0 + application logout, so the next login starts from a clean slate
 *    instead of spinning forever.
 *
 * The logout is inlined rather than reusing `useAuthClearSession` because this
 * hook runs in `Providers`, above the `<Router>`, where `useHistory` (pulled in
 * by `useAuthClearSession`) is unavailable.
 */
export function useAuth0SessionRecovery() {
  const { loginWithRedirect, logout: auth0Logout } = useAuth0();
  const auth0Context = useContext(Auth0Context);
  const getAuthToken = useGetAuthToken();

  const markSessionRecovered = useCallback(() => {
    sessionStorage.removeItem(RECOVERY_LOGIN_ATTEMPTED_KEY);
  }, []);

  const clearSession = useCallback(async () => {
    const returnTo = buildRedirectServiceReturnTo(window.location.origin);

    auth0Context?.setIsAuth0LogoutInProgress(true);

    await logout(getAuthToken, {
      emitLogout: true,
      emitAuthTokenInvalid: true,
    });
    await auth0Logout({ openUrl: false });
    auth0Logout({ logoutParams: { returnTo } });
  }, [auth0Logout, getAuthToken, auth0Context]);

  const recoverSession = useCallback(async () => {
    if (!hasStoredAuth0Session()) {
      // No session to recover (anonymous visitor) — leave normal flow alone.
      return;
    }

    if (sessionStorage.getItem(RECOVERY_LOGIN_ATTEMPTED_KEY)) {
      sessionStorage.removeItem(RECOVERY_LOGIN_ATTEMPTED_KEY);
      await clearSession();

      return;
    }

    sessionStorage.setItem(RECOVERY_LOGIN_ATTEMPTED_KEY, 'true');

    try {
      await loginWithRedirect();
    } catch {
      // loginWithRedirect threw before navigating away (e.g. SDK/network error
      // building the authorize URL). Fall straight through to a full logout +
      // clean login in this same load, so the user never sees a dead spinner
      // or has to reload / clear local storage themselves.
      sessionStorage.removeItem(RECOVERY_LOGIN_ATTEMPTED_KEY);
      await clearSession();
    }
  }, [loginWithRedirect, clearSession]);

  return { recoverSession, markSessionRecovered };
}
